import React from 'react';
import PropTypes from 'prop-types';

/**
 * Button Component (Enhanced for NuVola Website Migration)
 * 
 * A comprehensive button component covering all custom button patterns from the original design.
 * Supports hero buttons, download buttons, CTA buttons, and all legacy variants.
 */

const Button = ({
  children,
  variant = 'primary',
  size = 'default',
  disabled = false,
  loading = false,
  fullWidth = false,
  className = '',
  onClick,
  type = 'button',
  ...props
}) => {
  // Base styles that apply to all buttons
  const baseStyles = [
    'inline-flex',
    'items-center',
    'justify-center',
    'font-medium',
    'rounded',
    'border',
    'transition-all',
    'duration-300',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'disabled:transform-none',
    'cursor-pointer'
  ];

  // Variant styles based on design system and legacy patterns
  const variantStyles = {
    // Core variants (existing)
    primary: [
      'bg-nuvola-blue',
      'hover:bg-nuvola-blue-hover',
      'border-nuvola-blue',
      'text-white',
      'focus:ring-nuvola-blue',
      'shadow-sm',
      'hover:shadow-md'
    ],
    secondary: [
      'bg-nuvola-light-blue',
      'hover:bg-nuvola-light-blue-hover',
      'border-nuvola-light-blue',
      'text-gray-800',
      'focus:ring-nuvola-light-blue',
      'shadow-sm',
      'hover:shadow-md'
    ],
    action: [
      'bg-nuvola-primary',
      'hover:bg-nuvola-primary-hover',
      'border-nuvola-primary',
      'text-white',
      'focus:ring-nuvola-primary',
      'shadow-md',
      'hover:shadow-lg',
      'hover:-translate-y-0.5'
    ],
    outline: [
      'bg-transparent',
      'hover:bg-nuvola-blue',
      'border-nuvola-blue',
      'text-nuvola-blue',
      'hover:text-white',
      'focus:ring-nuvola-blue'
    ],
    ghost: [
      'bg-transparent',
      'hover:bg-gray-100',
      'border-transparent',
      'text-gray-700',
      'hover:text-gray-900',
      'focus:ring-gray-500'
    ],
    danger: [
      'bg-error',
      'hover:bg-error-hover',
      'border-error',
      'text-white',
      'focus:ring-error',
      'shadow-sm',
      'hover:shadow-md'
    ],
    
    // Legacy HomePage variants (primary-btn, secondary-btn patterns)
    'home-primary': [
      'bg-blue-800',     // #4970a3
      'hover:bg-blue-900', // #3c5e8a  
      'border-blue-800',
      'text-white',
      'focus:ring-blue-800',
      'shadow-sm',
      'font-medium'
    ],
    'home-secondary': [
      'bg-sky-400',      // #5cb3e7
      'hover:bg-sky-500', // #4a9dd1
      'border-sky-400',
      'text-white',
      'focus:ring-sky-400',
      'shadow-sm',
      'font-medium'
    ],
    
    // Hero button variants (used across multiple pages)
    'hero-blue': [
      'bg-blue-700',     // #3a73b5
      'hover:bg-blue-800', // #2c5a8e
      'border-blue-700',
      'text-white',
      'focus:ring-blue-700',
      'font-semibold',
      'px-5',
      'py-2',
      'rounded-md'
    ],
    'hero-light-blue': [
      'bg-sky-300',      // #87c1e8
      'hover:bg-sky-400', // #6eacd7
      'border-sky-300',
      'text-white',
      'focus:ring-sky-300',
      'font-semibold',
      'px-5',
      'py-2',
      'rounded-md'
    ],
    
    // CTA and special purpose variants
    'cta': [
      'bg-nuvola-blue',
      'hover:bg-nuvola-blue-hover',
      'border-nuvola-blue',
      'text-white',
      'focus:ring-nuvola-blue',
      'shadow-lg',
      'hover:shadow-xl',
      'font-semibold',
      'px-6',
      'py-3',
      'rounded-lg',
      'hover:-translate-y-1',
      'transform',
      'transition-all'
    ],
    
    // Download button variant
    'download': [
      'bg-nuvola-blue',
      'hover:bg-nuvola-blue-hover',
      'border-nuvola-blue',
      'text-white',
      'focus:ring-nuvola-blue',
      'shadow-md',
      'hover:shadow-lg',
      'font-medium',
      'px-6',
      'py-3',
      'rounded-lg'
    ],
    
    // Learn more style variant
    'learn-more': [
      'bg-nuvola-blue',
      'hover:bg-nuvola-blue-hover',
      'border-nuvola-blue',
      'text-white',
      'focus:ring-nuvola-blue',
      'shadow-sm',
      'hover:shadow-md',
      'font-medium',
      'px-4',
      'py-2',
      'rounded-md'
    ],
    
    // Topic/resource button variant
    'topic': [
      'bg-gray-100',
      'hover:bg-gray-200',
      'border-gray-300',
      'text-gray-700',
      'hover:text-gray-900',
      'focus:ring-gray-500',
      'font-medium',
      'px-4',
      'py-2',
      'rounded-md'
    ],
    
    // Support/contact button variants
    'support': [
      'bg-nuvola-blue',
      'hover:bg-nuvola-blue-hover',
      'border-nuvola-blue',
      'text-white',
      'focus:ring-nuvola-blue',
      'shadow-sm',
      'hover:shadow-md',
      'font-medium'
    ],
    
    // Disabled support variant
    'support-disabled': [
      'bg-gray-300',
      'border-gray-300',
      'text-gray-500',
      'cursor-not-allowed',
      'opacity-60'
    ]
  };

  // Size styles based on design system spacing
  const sizeStyles = {
    small: [
      'px-3',
      'py-1',
      'text-sm',
      'h-8'
    ],
    default: [
      'px-4',
      'py-2',
      'text-sm',
      'h-10'
    ],
    large: [
      'px-6',
      'py-3',
      'text-base',
      'h-12'
    ],
    xlarge: [
      'px-8',
      'py-4',
      'text-lg',
      'h-14'
    ],
    // Legacy sizes for backwards compatibility
    hero: [
      'px-5',
      'py-2',
      'text-sm',
      'h-10'
    ]
  };

  // Full width modifier
  const widthStyles = fullWidth ? ['w-full'] : [];

  // Loading state modifier
  const loadingStyles = loading ? ['cursor-wait'] : [];

  // For hero variants, override size styles since they have built-in sizing
  const isHeroVariant = ['hero-blue', 'hero-light-blue', 'cta', 'download', 'learn-more'].includes(variant);
  const appliedSizeStyles = isHeroVariant ? [] : (sizeStyles[size] || sizeStyles.default);

  // Combine all styles
  const buttonClasses = [
    ...baseStyles,
    ...(variantStyles[variant] || variantStyles.primary),
    ...(appliedSizeStyles || []),
    ...widthStyles,
    ...loadingStyles,
    className
  ].filter(Boolean).join(' ');

  // Loading spinner component
  const LoadingSpinner = () => (
    <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {children}
    </button>
  );
};

Button.propTypes = {
  /** Button content */
  children: PropTypes.node.isRequired,
  
  /** Button variant style */
  variant: PropTypes.oneOf([
    'primary',
    'secondary', 
    'action',
    'outline',
    'ghost',
    'danger',
    'home-primary',
    'home-secondary',
    'hero-blue',
    'hero-light-blue',
    'cta',
    'download',
    'learn-more',
    'topic',
    'support',
    'support-disabled'
  ]),
  
  /** Button size */
  size: PropTypes.oneOf(['small', 'default', 'large', 'xlarge', 'hero']),
  
  /** Disabled state */
  disabled: PropTypes.bool,
  
  /** Loading state with spinner */
  loading: PropTypes.bool,
  
  /** Full width button */
  fullWidth: PropTypes.bool,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Click handler */
  onClick: PropTypes.func,
  
  /** Button type */
  type: PropTypes.oneOf(['button', 'submit', 'reset'])
};

Button.defaultProps = {
  variant: 'primary',
  size: 'default',
  disabled: false,
  loading: false,
  fullWidth: false,
  className: '',
  type: 'button'
};

export default Button; 